import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { ModuleGuard } from '@/components/module-guard'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

const componentSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  code: z.string().min(1, 'Code is required'),
  type: z.enum(['earning', 'deduction']),
  isTaxable: z.boolean().optional(),
  isFixed: z.boolean().optional(),
  description: z.string().optional(),
})

type ComponentFormValues = z.infer<typeof componentSchema>

type SalaryComponent = {
  id: number
  name: string
  code: string
  type: 'earning' | 'deduction'
  isTaxable?: boolean | null
  isFixed?: boolean | null
  description?: string | null
}

export const Route = createFileRoute('/hr/payroll/components')({
  component: SalaryComponentsPage,
})

function SalaryComponentsPage() {
  return (
    <ModuleGuard moduleKey="hr">
      <SalaryComponentsList />
    </ModuleGuard>
  )
}

function SalaryComponentsList() {
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingComponent, setEditingComponent] = useState<SalaryComponent | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: number | null }>({
    open: false,
    id: null,
  })

  const queryClient = useQueryClient()

  const { data: componentsData, isLoading } = useQuery(
    orpc.hr.listSalaryComponents.queryOptions({
      input: {
        type: typeFilter === 'all' ? undefined : (typeFilter as 'earning' | 'deduction'),
      },
    }),
  )

  const components = (componentsData ?? []) as SalaryComponent[]

  const createMutation = useMutation(
    orpc.hr.createSalaryComponent.mutationOptions({
      onSuccess: () => {
        toast.success('Component created')
        queryClient.invalidateQueries({ queryKey: [['hr', 'listSalaryComponents']] })
        setIsFormOpen(false)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const updateMutation = useMutation(
    orpc.hr.updateSalaryComponent.mutationOptions({
      onSuccess: () => {
        toast.success('Component updated')
        queryClient.invalidateQueries({ queryKey: [['hr', 'listSalaryComponents']] })
        setIsFormOpen(false)
        setEditingComponent(null)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const deleteMutation = useMutation(
    orpc.hr.deleteSalaryComponent.mutationOptions({
      onSuccess: () => {
        toast.success('Component deleted')
        queryClient.invalidateQueries({ queryKey: [['hr', 'listSalaryComponents']] })
        setDeleteDialog({ open: false, id: null })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const handleEdit = (component: SalaryComponent) => {
    setEditingComponent(component)
    setIsFormOpen(true)
  }

  const handleSubmit = async (data: ComponentFormValues) => {
    if (editingComponent) {
      await updateMutation.mutateAsync({ id: editingComponent.id, ...data })
    } else {
      await createMutation.mutateAsync(data)
    }
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Salary Components</h1>
          <p className="text-muted-foreground">Manage earnings and deduction components</p>
        </div>
        <Button
          onClick={() => {
            setEditingComponent(null)
            setIsFormOpen(true)
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          New Component
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex gap-4">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="earning">Earning</SelectItem>
                <SelectItem value="deduction">Deduction</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : components.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No salary components found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Taxable</TableHead>
                  <TableHead>Fixed</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {components.map((component) => (
                  <TableRow key={component.id}>
                    <TableCell className="font-medium">{component.name}</TableCell>
                    <TableCell>
                      <code className="font-mono text-sm bg-muted px-1.5 py-0.5 rounded">
                        {component.code}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={
                          component.type === 'earning'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }
                        variant="outline"
                      >
                        {component.type === 'earning' ? 'Earning' : 'Deduction'}
                      </Badge>
                    </TableCell>
                    <TableCell>{component.isTaxable ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{component.isFixed ? 'Fixed' : 'Variable'}</TableCell>
                    <TableCell className="max-w-xs truncate text-muted-foreground">
                      {component.description ?? '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(component)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600"
                          onClick={() => setDeleteDialog({ open: true, id: component.id })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ComponentFormDialog
        open={isFormOpen}
        onOpenChange={(open) => {
          setIsFormOpen(open)
          if (!open) setEditingComponent(null)
        }}
        component={editingComponent}
        onSubmit={handleSubmit}
      />

      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, id: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Component</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this component? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteMutation.isPending}
              onClick={() => deleteDialog.id && deleteMutation.mutate({ id: deleteDialog.id })}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

interface ComponentFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  component: SalaryComponent | null
  onSubmit: (data: ComponentFormValues) => Promise<void>
}

function ComponentFormDialog({ open, onOpenChange, component, onSubmit }: ComponentFormDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<ComponentFormValues>({
    resolver: zodResolver(componentSchema),
    defaultValues: {
      name: component?.name ?? '',
      code: component?.code ?? '',
      type: (component?.type ?? 'earning') as 'earning' | 'deduction',
      isTaxable: component?.isTaxable ?? false,
      isFixed: component?.isFixed ?? true,
      description: component?.description ?? '',
    },
  })

  // Re-initialize when component changes
  const handleOpenChange = (open: boolean) => {
    if (open && component) {
      form.reset({
        name: component.name,
        code: component.code,
        type: component.type,
        isTaxable: component.isTaxable ?? false,
        isFixed: component.isFixed ?? true,
        description: component.description ?? '',
      })
    } else if (!open) {
      form.reset({ name: '', code: '', type: 'earning', isTaxable: false, isFixed: true, description: '' })
    }
    onOpenChange(open)
  }

  const handleSubmit = async (data: ComponentFormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit(data)
      form.reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{component ? 'Edit Component' : 'New Salary Component'}</DialogTitle>
          <DialogDescription>
            {component ? 'Update salary component details' : 'Create a new earning or deduction component'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Basic Salary" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Code *</FormLabel>
                  <FormControl>
                    <Input placeholder="BASIC" className="font-mono" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type *</FormLabel>
                  <Select
                    onValueChange={(v) => field.onChange(v as 'earning' | 'deduction')}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="earning">Earning</SelectItem>
                      <SelectItem value="deduction">Deduction</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-6">
              <FormField
                control={form.control}
                name="isTaxable"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value ?? false}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="font-normal">Taxable</FormLabel>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isFixed"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value ?? false}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="font-normal">Fixed Amount</FormLabel>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Optional description..." rows={2} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : component ? 'Update Component' : 'Create Component'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
